import { useEffect, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api';
import type { FrontendFrameworkAdapterView, ProjectApplication, UpdateComponentSettingsInput } from '../api';
import { Input, Select } from './primitives';

/**
 * BuildSettingsFields: how Commerce Ops installs, builds and starts one
 * storefront (CO-90, audit F2). Add frontend, the build-settings drawer and
 * Component settings all edit these same settings, so they all render this
 * one set of fields: same labels, same controls, same order, same hints.
 * Each form keeps its own fields (name, repository, health check…) around it.
 *
 * The install command is not a field: the server accepts only the one safe
 * form per package manager (`npm ci`, `pnpm install --frozen-lockfile`,
 * `yarn install --immutable`) and refuses anything else, so it is shown as
 * what follows from the package manager.
 */

export interface BuildSettings {
  framework: string;
  packageManager: string;
  /** The Node.js major version, without the "node" prefix ("22"). */
  nodeVersion: string;
  buildScript: string;
  startScript: string;
  workingDirectory: string;
  /** 'node_server' or 'static_files'. */
  outputMode: string;
}

export const packageManagers = ['npm', 'pnpm', 'yarn'] as const;
const nodeVersions = ['22', '20', '18'];

/** The one install command the server runs for a package manager. */
export function installCommandFor(packageManager: string): string {
  switch (packageManager.trim().toLowerCase()) {
    case 'pnpm':
      return 'pnpm install --frozen-lockfile';
    case 'yarn':
      return 'yarn install --immutable';
    default:
      return 'npm ci';
  }
}

/** "node20", "20.11.1", ">=18" → "20" / "18"; anything without a number is kept. */
export function nodeMajor(value: string | undefined | null): string {
  const raw = (value ?? '').trim();
  const match = raw.match(/(\d+)/);
  return match ? match[1]! : raw;
}

/** The settings a new frontend starts from before its repository is read. */
export const defaultBuildSettings: BuildSettings = {
  framework: '',
  packageManager: 'npm',
  nodeVersion: '22',
  buildScript: 'build',
  startScript: 'start',
  workingDirectory: '/',
  outputMode: 'node_server',
};

/** The settings of an existing component. */
export function buildSettingsOf(app: ProjectApplication): BuildSettings {
  return {
    framework: app.framework ?? '',
    packageManager: app.package_manager || 'npm',
    nodeVersion: nodeMajor(app.runtime) || '22',
    buildScript: app.build_script || 'build',
    startScript: app.start_script || 'start',
    workingDirectory: app.working_directory || '/',
    outputMode: app.output_runtime_type || 'node_server',
  };
}

/** The settings as the component API stores them. */
export function buildSettingsInput(settings: BuildSettings): Required<
  Pick<UpdateComponentSettingsInput, 'framework' | 'runtime' | 'package_manager' | 'install_command' | 'build_script' | 'start_script' | 'working_directory' | 'output_runtime_type'>
> {
  return {
    framework: settings.framework,
    runtime: `node${nodeMajor(settings.nodeVersion) || '22'}`,
    package_manager: settings.packageManager,
    install_command: installCommandFor(settings.packageManager),
    build_script: settings.buildScript.trim() || 'build',
    start_script: settings.startScript.trim() || 'start',
    working_directory: settings.workingDirectory.trim() || '/',
    output_runtime_type: settings.outputMode,
  };
}

/** The frameworks Commerce Ops has an adapter for, read from the adapters
 * themselves so a new one appears without a dashboard change. */
export function useFrontendFrameworks(enabled = true): FrontendFrameworkAdapterView[] {
  const [frameworks, setFrameworks] = useState<FrontendFrameworkAdapterView[]>([]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .listFrontendFrameworks()
      .then((result) => {
        if (!cancelled) setFrameworks(result.frameworks ?? []);
      })
      .catch(() => {
        /* the stored or detected framework is still shown and kept */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return frameworks;
}

function BuildField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: (ids: { id: string; hintId?: string }) => ReactNode;
}) {
  const id = useId();
  const hintId = useId();
  return (
    <div className="field build-field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, hintId: hint ? hintId : undefined })}
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function BuildSettingsFields({
  value,
  onChange,
  frameworks,
  testIdPrefix,
  disabled = false,
  requireFramework = false,
}: {
  value: BuildSettings;
  onChange: (next: BuildSettings) => void;
  frameworks: FrontendFrameworkAdapterView[];
  /** Prefixes each control's data-testid: `${prefix}-start`, `${prefix}-framework`… */
  testIdPrefix: string;
  disabled?: boolean;
  /** Add frontend cannot save without a framework. */
  requireFramework?: boolean;
}) {
  const set = <K extends keyof BuildSettings>(key: K, next: BuildSettings[K]) => onChange({ ...value, [key]: next });
  const nodeOptions = value.nodeVersion && !nodeVersions.includes(value.nodeVersion) ? [value.nodeVersion, ...nodeVersions] : nodeVersions;
  const knownFramework = frameworks.some((f) => f.framework === value.framework);

  return (
    <div className="build-fields" data-testid={`${testIdPrefix}-build-fields`}>
      <BuildField label="Framework">
        {({ id, hintId }) => (
          <Select
            id={id}
            aria-describedby={hintId}
            value={value.framework}
            onChange={(event) => set('framework', event.target.value)}
            required={requireFramework}
            disabled={disabled}
            data-testid={`${testIdPrefix}-framework`}
          >
            {value.framework === '' ? <option value="">Not detected: choose one</option> : null}
            {frameworks.map((f) => (
              <option key={f.framework} value={f.framework}>
                {f.label}
              </option>
            ))}
            {value.framework && !knownFramework ? <option value={value.framework}>{value.framework}</option> : null}
          </Select>
        )}
      </BuildField>

      <BuildField
        label="Package manager"
        hint={
          <>
            Install: <code>{installCommandFor(value.packageManager)}</code>
          </>
        }
      >
        {({ id, hintId }) => (
          <Select
            id={id}
            aria-describedby={hintId}
            value={value.packageManager}
            onChange={(event) => set('packageManager', event.target.value)}
            disabled={disabled}
            data-testid={`${testIdPrefix}-package-manager`}
          >
            {packageManagers.map((pm) => (
              <option key={pm} value={pm}>
                {pm}
              </option>
            ))}
            {!packageManagers.includes(value.packageManager as (typeof packageManagers)[number]) ? (
              <option value={value.packageManager}>{value.packageManager}</option>
            ) : null}
          </Select>
        )}
      </BuildField>

      <BuildField label="Node.js version">
        {({ id }) => (
          <Select
            id={id}
            value={value.nodeVersion}
            onChange={(event) => set('nodeVersion', event.target.value)}
            disabled={disabled}
            data-testid={`${testIdPrefix}-node`}
          >
            {nodeOptions.map((v) => (
              <option key={v} value={v}>
                Node.js {v}
              </option>
            ))}
          </Select>
        )}
      </BuildField>

      <BuildField label="Build script" hint="A package.json script name. Shell commands are refused.">
        {({ id, hintId }) => (
          <Input
            id={id}
            aria-describedby={hintId}
            type="text"
            className="input--mono"
            value={value.buildScript}
            onChange={(event) => set('buildScript', event.target.value)}
            placeholder="build"
            spellCheck={false}
            autoComplete="off"
            disabled={disabled}
            data-testid={`${testIdPrefix}-build`}
          />
        )}
      </BuildField>

      <BuildField
        label="Start script"
        hint={
          <>
            Runs <code>{value.packageManager || 'npm'} run {value.startScript || 'start'}</code>
          </>
        }
      >
        {({ id, hintId }) => (
          <Input
            id={id}
            aria-describedby={hintId}
            type="text"
            className="input--mono"
            value={value.startScript}
            onChange={(event) => set('startScript', event.target.value)}
            placeholder="start"
            spellCheck={false}
            autoComplete="off"
            disabled={disabled}
            data-testid={`${testIdPrefix}-start`}
          />
        )}
      </BuildField>

      <BuildField
        label="Working directory"
        hint={
          <>
            <code>/</code> for the repository root, or the app’s folder in a monorepo.
          </>
        }
      >
        {({ id, hintId }) => (
          <Input
            id={id}
            aria-describedby={hintId}
            type="text"
            className="input--mono"
            value={value.workingDirectory}
            onChange={(event) => set('workingDirectory', event.target.value)}
            placeholder="/"
            spellCheck={false}
            autoComplete="off"
            disabled={disabled}
            data-testid={`${testIdPrefix}-workdir`}
          />
        )}
      </BuildField>

      <BuildField label="Build output">
        {({ id }) => (
          <Select
            id={id}
            value={value.outputMode}
            onChange={(event) => set('outputMode', event.target.value)}
            disabled={disabled}
            data-testid={`${testIdPrefix}-output`}
          >
            <option value="node_server">Node server</option>
            <option value="static_files">Static files</option>
          </Select>
        )}
      </BuildField>
    </div>
  );
}
